import { useEffect, useState } from 'react';
import { useSettingsStore } from '../../stores/settings-store';
import { useProjectsStore, type GenerationSettings } from '../../stores/projects-store';
import { useEditorStore } from '../../stores/editor-store';
import { Dialog } from '../ui/Dialog';
import { Button } from '../ui/Button';
import { Check, Smartphone, Tablet, Monitor, Palette } from 'lucide-react';
import clsx from 'clsx';

const PRESET_COLORS = [
    '#3b82f6', // Blue
    '#10b981', // Emerald
    '#ef4444', // Red
    '#8b5cf6', // Violet
    '#f97316', // Orange
    '#eab308', // Yellow
    '#14b8a6', // Teal
    '#ec4899', // Pink
    '#64748b', // Slate
    '#6366f1', // Indigo
];

const DEFAULT_SETTINGS: GenerationSettings = {
    devices: ['mobile'],
    detailLevel: 3,
    colors: [],
    backgroundColor: '',
};

export function GenerationSettingsDialog() {
    const open = useSettingsStore((s) => s.generationSettingsOpen);
    const setOpen = useSettingsStore((s) => s.setGenerationSettingsOpen);

    const currentProject = useProjectsStore((s) => s.currentProject);
    const updateProject = useProjectsStore((s) => s.updateProject);
    const applyAppBackground = useEditorStore((s) => s.applyAppBackground);

    const [settings, setSettings] = useState<GenerationSettings>(DEFAULT_SETTINGS);

    // Load settings when dialog opens or project changes
    useEffect(() => {
        if (open && currentProject) {
            if (currentProject.generationSettings) {
                // Merge with defaults to ensure all fields exist
                setSettings({
                    ...DEFAULT_SETTINGS,
                    ...currentProject.generationSettings
                });
            } else {
                setSettings(DEFAULT_SETTINGS);
            }
        }
    }, [open, currentProject?.id]);

    const handleSave = async () => {
        try {
            if (currentProject) {
                await updateProject(currentProject.id, { generationSettings: settings });
                // Apply immediately so the canvas doesn't keep an old/black root background until next load.
                applyAppBackground(settings.backgroundColor);
            }
        } catch (error) {
            console.error('Failed to save settings:', error);
        } finally {
            setOpen(false);
        }
    };

    const toggleDevice = (device: 'mobile' | 'tablet' | 'desktop') => {
        setSettings(prev => {
            const devices = new Set(prev.devices);
            if (devices.has(device)) {
                devices.delete(device);
            } else {
                devices.add(device);
            }
            // Ensure at least one device is selected? Or allow none (implies generic)?
            // Better ensure at least one.
            const newDevices = Array.from(devices) as ('mobile' | 'tablet' | 'desktop')[];
            if (newDevices.length === 0) return prev;
            return { ...prev, devices: newDevices };
        });
    };

    const toggleColor = (color: string) => {
        setSettings(prev => {
            const colors = new Set(prev.colors);
            if (colors.has(color)) {
                colors.delete(color);
            } else {
                if (colors.size >= 5) return prev; // Max 5
                colors.add(color);
            }
            return { ...prev, colors: Array.from(colors) };
        });
    };

    const setBackgroundColor = (color: string) => {
        const next = (color || '').trim();
        setSettings(prev => ({ ...prev, backgroundColor: next }));
    };

    return (
        <Dialog open={open} onClose={() => setOpen(false)} title="Generation Settings">
            <div className="flex flex-col gap-6">

                {/* Device Selection */}
                <div className="flex flex-col gap-2">
                    <label className="text-sm font-medium text-text flex items-center gap-2">
                        Target Devices
                        <span className="text-xs font-normal text-text-muted">(Multi-select)</span>
                    </label>
                    <div className="flex gap-2">
                        {[
                            { id: 'mobile', icon: Smartphone, label: 'Mobile' },
                            { id: 'tablet', icon: Tablet, label: 'Tablet' },
                            { id: 'desktop', icon: Monitor, label: 'Desktop' },
                        ].map(({ id, icon: Icon, label }) => {
                            const isSelected = settings.devices.includes(id as any);
                            return (
                                <button
                                    key={id}
                                    onClick={() => toggleDevice(id as any)}
                                    className={clsx(
                                        "flex items-center gap-2 px-3 py-2 rounded border transition-colors",
                                        isSelected
                                            ? "bg-accent/10 border-accent text-accent"
                                            : "bg-surface border-surface-lighter text-text-muted hover:border-accent/50 hover:text-text"
                                    )}
                                >
                                    <Icon size={16} />
                                    <span className="text-sm">{label}</span>
                                    {isSelected && <Check size={12} />}
                                </button>
                            )
                        })}
                    </div>
                </div>

                {/* Detail Level */}
                <div className="flex flex-col gap-2">
                    <div className="flex justify-between items-center">
                        <label className="text-sm font-medium text-text">Detail Level</label>
                        <span className="text-xs text-text-muted px-2 py-0.5 bg-surface-lighter rounded">
                            Level {settings.detailLevel}
                        </span>
                    </div>

                    <input
                        type="range"
                        min="1"
                        max="5"
                        step="1"
                        value={settings.detailLevel}
                        onChange={(e) => setSettings(s => ({ ...s, detailLevel: parseInt(e.target.value) }))}
                        className="w-full h-1 bg-surface-lighter rounded-lg appearance-none cursor-pointer accent-accent"
                    />
                    <div className="flex justify-between text-[10px] text-text-muted mt-1">
                        <span>Simple (Skeletal)</span>
                        <span>High Fidelity</span>
                    </div>
                </div>

                {/* Color Palette */}
                <div className="flex flex-col gap-2">
                    <label className="text-sm font-medium text-text flex items-center gap-2">
                        Key Colors
                        <Palette size={14} className="text-text-muted" />
                        <span className="text-xs font-normal text-text-muted">(Max 5)</span>
                    </label>
                    <div className="flex flex-wrap gap-2">
                        {PRESET_COLORS.map(color => {
                            const isSelected = settings.colors.includes(color);
                            return (
                                <button
                                    key={color}
                                    onClick={() => toggleColor(color)}
                                    className={clsx(
                                        "w-8 h-8 rounded-full border-2 flex items-center justify-center transition-all",
                                        isSelected ? "border-white scale-110 shadow-md" : "border-transparent hover:scale-105"
                                    )}
                                    style={{ backgroundColor: color }}
                                    title={color}
                                >
                                    {isSelected && <Check size={14} className="text-white drop-shadow-md" strokeWidth={3} />}
                                </button>
                            )
                        })}
                    </div>
                    {settings.colors.length === 0 && (
                        <p className="text-xs text-text-muted italic">No colors selected. AI will choose a palette.</p>
                    )}
                </div>

                {/* App Background */}
                <div className="flex flex-col gap-2">
                    <label className="text-sm font-medium text-text">App Background</label>
                    <div className="flex items-center gap-3">
                        <input
                            type="color"
                            value={settings.backgroundColor || '#0b1220'}
                            onChange={(e) => setBackgroundColor(e.target.value)}
                            className="h-9 w-14 rounded border border-surface-lighter bg-surface p-1"
                            title="Pick background color"
                        />
                        <input
                            type="text"
                            value={settings.backgroundColor || ''}
                            onChange={(e) => setBackgroundColor(e.target.value)}
                            placeholder="#0b1220"
                            className="flex-1 px-2 py-2 bg-surface border border-surface-lighter rounded text-xs text-text placeholder:text-text-muted focus:outline-none focus:border-accent"
                        />
                        <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => setBackgroundColor('')}
                            title="Use default background"
                        >
                            Reset
                        </Button>
                    </div>
                    <p className="text-xs text-text-muted">
                        Used as a style constraint for Generate/Improve (root screen background, edge-to-edge). Existing screens won't be recolored automatically; use Improve after changing this.
                    </p>
                </div>

                <div className="flex justify-end gap-2 pt-4 border-t border-surface-lighter">
                    <Button variant="secondary" onClick={() => setOpen(false)}>
                        Cancel
                    </Button>
                    <Button onClick={handleSave}>
                        Save Settings
                    </Button>
                </div>
            </div>
        </Dialog>
    );
}
